import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Card,
  Divider,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import FavoriteIcon from "@mui/icons-material/Favorite";

import Header from "./Header";
import Footer from "./Footer";

const TABS = ["Syllabus", "Notes", "Papers"];
const TAB_KEYS = { 0: "syllabus", 1: "notes", 2: "papers" };

export default function Favourite() {
  const [favorites, setFavorites] = useState([]);
  const [category, setCategory] = useState("sslc");
  const [activeTab, setActiveTab] = useState(0);
  const [pdfData, setPdfData] = useState([]);
  const [removeAlert, setRemoveAlert] = useState("");

  const categories = {
    sslc: { label: "🧑‍🎓 SSLC " },
    puc: { label: "🎓 PUC/11th-12th " },
    ug: { label: "🎓 UG " },
    pg: { label: "🎓 PG " },
    engineering: { label: "🏗️ Engineering" },
    exams: { label: "📝 Exams" },
  };

  // Load favorites from localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const savedFavorites = JSON.parse(
          localStorage.getItem("favorites") || "[]"
        );
        setFavorites(savedFavorites);
      } catch {
        setFavorites([]);
      }
    }
  }, []);

  // Filter PDFs by category + tab
  useEffect(() => {
    if (favorites.length > 0) {
      const filtered = favorites.filter((pdf) => {
        if (pdf.category !== category) return false;
        if (pdf.tab && pdf.tab !== TAB_KEYS[activeTab]) return false;
        return true;
      });
      setPdfData(filtered);
    } else {
      setPdfData([]);
    }
  }, [favorites, category, activeTab]);

  // 🚀 NEW: Remove from favorites (syncs with Categories page)
  const handleRemoveFavorite = (pdfId) => {
    try {
      // 1. Filter out the removed PDF from current favorites
      const updatedFavorites = favorites.filter((pdf) => pdf.id !== pdfId);

      // 2. Save to localStorage (Categories page reads from here)
      localStorage.setItem("favorites", JSON.stringify(updatedFavorites));

      // 3. Update state
      setFavorites(updatedFavorites);

      // 4. Show success message
      setRemoveAlert(`Removed from ${categories[category].label}!`);

      // 5. Hide alert after 3 seconds
      setTimeout(() => setRemoveAlert(""), 3000);
    } catch (error) {
      console.error("Error removing favorite:", error);
    }
  };

  const handleOpenPdf = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  const handleChangeCategory = (value) => {
    setCategory(value);
  };

  const renderMainContent = () => {
    return (
      <Box>
        {/* Success Alert */}
        {removeAlert && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setRemoveAlert("")}
          >
            {removeAlert}
          </Alert>
        )}
        <Box mb={4}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            centered
            variant="fullWidth"
            sx={{
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 3,
                background:
                  "linear-gradient(90deg, #2563EB 0%, #10B981 50%, #F97316 100%)",
              },
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
            }}
          >
            {TABS.map((tab, index) => {
              const count = favorites.filter(
                (pdf) =>
                  pdf.category === category &&
                  (!pdf.tab || pdf.tab === TAB_KEYS[index])
              ).length;
              return (
                <Tab
                  key={index}
                  label={
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ fontSize: 15 }}
                    >
                      {tab}
                      <Chip label={`(${count})`} size="small" />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>{" "}
        </Box>

        {pdfData.length === 0 ? (
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: "center", fontStyle: "italic", mt: 10 }}
          >
            No {TABS[activeTab].toLowerCase()} in {categories[category].label}{" "}
            yet. ⭐<br />
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {pdfData.map((pdf) => (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} key={pdf.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    // p: 3,
                    textAlign: "left",
                    borderRadius: 5,
                    boxShadow: 12,

                    borderLeft: "4px solid #1A4B83",
                    borderRight: "4px solid #1A4B83",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      pb: 2,
                      bgcolor: "#F8FAFC",
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      gap={2}
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleOpenPdf(pdf.fileUrl)}
                    >
                      <PictureAsPdfIcon
                        sx={{ fontSize: 25, color: "#DC2626", mt: 0.5 }}
                      />
                      <Box flex={1}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          noWrap
                          sx={{ mb: 0.5, fontSize: { xs: 16, md: 17 } }}
                        >
                          {pdf.title}
                        </Typography>

                        {pdf.subject && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              fontSize: { xs: 14, md: 16 },
                            }}
                          >
                            📂 {pdf.subject} ({pdf.level})
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFavorite(pdf.id);
                        }}
                        title="Remove from favorites"
                      >
                        <FavoriteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          p: { xs: 2, md: 6 },
          mt: 10,
          minHeight: "85vh",
          bgcolor: "#F9FAFB",
        }}
      >
        <Grid container spacing={3}>
          {/* LEFT SIDEBAR */}
          <Grid size={{ xs: 12, md: 2.5 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              mb={3}
              sx={{ fontSize: 24 }}
            >
              ⭐ Favourites
            </Typography>
            <RadioGroup
              value={category}
              onChange={(e) => handleChangeCategory(e.target.value)}
            >
              {Object.entries(categories).map(([key, value]) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio size="small" />}
                  label={<Typography variant="body1">{value.label}</Typography>}
                  sx={{ mb: 2 }}
                />
              ))}
            </RadioGroup>
          </Grid>

          <Grid
            size={{ xs: 0, md: 0.5 }}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: "100%", width: 2, bgcolor: "#E2E8F0" }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 9 }}>
            <Typography variant="h6" color="text.secondary" mb={2}>
              Total Favourites: <strong>{favorites.length}</strong>
            </Typography>
            {renderMainContent()}
          </Grid>
        </Grid>
      </Box>
      <Footer />
    </>
  );
}

